/**
 * SegmentBuilder — Dynamic segment CRUD + visual AND/OR filter builder
 * Phase B.4 (Communications audit)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, IconButton, TextField,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Divider, Stack, Tooltip, Switch, FormControlLabel,
  Alert, Table, TableBody, TableCell, TableHead, TableRow, Paper
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
  FilterList as FilterIcon, Preview as PreviewIcon, Lock as LockIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import type { MenuProps } from '@mui/material/Menu';

/** Inner editor Dialog root is z-index 1400; MUI Menu defaults to ~1300 and paints behind it */
const SEGMENT_BUILDER_SELECT_MENU_PROPS: Partial<MenuProps> = {
  disableScrollLock: true,
  slotProps: {
    root: {
      sx: { zIndex: 1600 },
    },
    paper: {
      sx: { zIndex: 1600 },
    },
  },
};

type Op =
  | 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte'
  | 'in' | 'not_in'
  | 'is_null' | 'is_not_null'
  | 'contains' | 'starts_with' | 'ends_with'
  | 'within_days' | 'before_days' | 'after_days';

interface Condition {
  field: string;
  op: Op;
  value?: unknown;
  aggregate?: 'any' | 'none' | 'count';
}
interface Group {
  op: 'AND' | 'OR';
  conditions: Array<Group | Condition>;
}

interface Segment {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  is_active: boolean;
  is_system: boolean;
  last_refreshed_at: string | null;
  last_count: number | null;
  refresh_error: string | null;
  created_at: string;
  updated_at: string;
}

interface Schema {
  fields: Array<{ field: string; type: string }>;
  aggregateFields: string[];
  ops: string[];
  aggregates: string[];
}

const EMPTY_FILTER: Group = {
  op: 'AND',
  conditions: [{ field: 'users.is_active', op: 'eq', value: true }]
};

/** API / starters may omit `conditions` or use null; GroupEditor requires an array. */
function normalizeFilterGroup(raw: unknown): Group {
  if (!raw || typeof raw !== 'object') return EMPTY_FILTER;
  const n = raw as { op?: unknown; conditions?: unknown };
  if (n.op !== 'AND' && n.op !== 'OR') return EMPTY_FILTER;
  const rawList = Array.isArray(n.conditions) ? n.conditions : [];
  const conditions: Array<Group | Condition> = rawList.map((item: unknown) => {
    if (!item || typeof item !== 'object') {
      return { field: 'users.is_active', op: 'eq' as Op, value: true };
    }
    const o = item as { op?: unknown };
    if (o.op === 'AND' || o.op === 'OR') {
      return normalizeFilterGroup(item);
    }
    return item as Condition;
  });
  return { op: n.op as 'AND' | 'OR', conditions };
}

export interface SegmentBuilderProps {
  /**
   * Optional starting filter used when the editor opens for a fresh segment.
   * Lets the surrounding page (e.g. SegmentBuilderDialog) prefill the tree
   * from a starter template without changing the builder's logic.
   */
  initialFilter?: Group;
  /**
   * Optional default name used when the editor opens for a fresh segment —
   * paired with initialFilter for starter templates.
   */
  initialName?: string;
  /**
   * If true, the editor opens automatically on mount. Used by the dialog
   * variant so operators see the form immediately instead of an empty list.
   */
  autoOpenEditor?: boolean;
  /**
   * If set, after the segment list loads the editor opens for this segment id
   * (deep link from marketing audience card "Edit").
   */
  initialEditSegmentId?: string;
}

export type SegmentBuilderInitialFilter = Group;

const SegmentBuilder: React.FC<SegmentBuilderProps> = ({
  initialFilter,
  initialName,
  autoOpenEditor = false,
  initialEditSegmentId,
}) => {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState<Schema | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Segment | null>(null);
  const [form, setForm] = useState<{ name: string; description: string; filter_json: Group }>({
    name: initialName || '',
    description: '',
    filter_json: normalizeFilterGroup(initialFilter),
  });
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
  const [surfaceError, setSurfaceError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, schemaRes] = await Promise.all([
        apiService.listDynamicSegments(),
        apiService.getDynamicSegmentSchema(),
      ]);
      setSegments(listRes?.data || []);
      setSchema(schemaRes?.data || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (autoOpenEditor) setEditorOpen(true);
  }, [autoOpenEditor]);

  const allFields = useMemo(() => {
    if (!schema) return [] as Array<{ field: string; aggregate?: boolean }>;
    const fields = Array.isArray(schema.fields) ? schema.fields : [];
    const aggregateFields = Array.isArray(schema.aggregateFields) ? schema.aggregateFields : [];
    return [
      ...fields.map(f => ({ field: f.field })),
      ...aggregateFields.map(f => ({ field: f, aggregate: true })),
    ];
  }, [schema]);

  const openCreate = () => {
    setSurfaceError(null);
    setEditing(null);
    setForm({ name: '', description: '', filter_json: EMPTY_FILTER });
    setPreviewCount(null);
    setPreviewError(null);
    setEditorOpen(true);
  };

  const openEdit = useCallback(async (seg: Segment) => {
    setSurfaceError(null);
    setEditLoadingId(seg.id);
    try {
      const res = await apiService.getDynamicSegment(seg.id);
      const full = res?.data as {
        name?: string;
        description?: string | null;
        filter_json?: unknown;
      } | null;
      if (!full || typeof full !== 'object') {
        setSurfaceError('Could not load segment details from the server.');
        return;
      }
      setEditing(seg);
      setForm({
        name: full.name ?? seg.name,
        description: full.description ?? '',
        filter_json: normalizeFilterGroup(full.filter_json),
      });
      setPreviewCount(seg.last_count);
      setPreviewError(null);
      setEditorOpen(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load segment';
      setSurfaceError(msg);
    } finally {
      setEditLoadingId(null);
    }
  }, []);

  const deepLinkEditOpenedFor = useRef<string | null>(null);

  useEffect(() => {
    deepLinkEditOpenedFor.current = null;
  }, [initialEditSegmentId]);

  useEffect(() => {
    if (!initialEditSegmentId || loading) return;
    const seg = segments.find((s) => s.id === initialEditSegmentId);
    if (!seg) {
      setSurfaceError('This audience was not found in the segment list. It may have been deleted.');
      return;
    }
    if (deepLinkEditOpenedFor.current === initialEditSegmentId) return;
    deepLinkEditOpenedFor.current = initialEditSegmentId;
    void openEdit(seg);
  }, [initialEditSegmentId, loading, segments, openEdit]);

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await apiService.previewDynamicSegment(form.filter_json as unknown as Record<string, unknown>);
      setPreviewCount(res?.data?.count ?? 0);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Preview failed';
      setPreviewError(msg);
      setPreviewCount(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) { setPreviewError('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await apiService.updateDynamicSegment(editing.id, {
          name: form.name,
          description: form.description || null,
          filter_json: form.filter_json as unknown as Record<string, unknown>,
        });
      } else {
        await apiService.createDynamicSegment({
          name: form.name,
          description: form.description,
          filter_json: form.filter_json as unknown as Record<string, unknown>,
        });
      }
      setEditorOpen(false);
      await load();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      setPreviewError(msg);
    } finally {
      setSaving(false);
    }
  };

  const refresh = async (id: string) => {
    setRefreshingId(id);
    try {
      await apiService.refreshDynamicSegment(id);
      await load();
    } finally {
      setRefreshingId(null);
    }
  };

  const toggleActive = async (seg: Segment) => {
    await apiService.updateDynamicSegment(seg.id, { is_active: !seg.is_active });
    await load();
  };

  const remove = async (seg: Segment) => {
    if (seg.is_system) return;
    if (!window.confirm(`Delete segment "${seg.name}"?`)) return;
    await apiService.deleteDynamicSegment(seg.id);
    await load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon sx={{ color: '#c2410c' }} /> Dynamic Segments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            filter_json DSL over users, engagement, email activity, and Stripe. Memberships refresh every 15 minutes via the segment-refresh worker.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ bgcolor: '#c2410c', '&:hover': { bgcolor: '#9a3412' } }}>
          New Segment
        </Button>
      </Box>

      {surfaceError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSurfaceError(null)}>
          {surfaceError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: '12px' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell align="right">Members</TableCell>
                <TableCell>Last refreshed</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {segments.map(seg => (
                <TableRow key={seg.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {seg.is_system && <Tooltip title="System segment"><LockIcon fontSize="small" color="action" /></Tooltip>}
                      <Typography sx={{ fontWeight: 500 }}>{seg.name}</Typography>
                    </Box>
                    {seg.description && (
                      <Typography variant="caption" color="text.secondary">{seg.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell><code style={{ fontSize: '0.75rem' }}>{seg.slug}</code></TableCell>
                  <TableCell align="right">
                    {seg.last_count !== null ? seg.last_count.toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    {seg.last_refreshed_at ? new Date(seg.last_refreshed_at).toLocaleString() : 'never'}
                  </TableCell>
                  <TableCell>
                    {seg.refresh_error ? (
                      <Chip size="small" color="error" label="error" />
                    ) : seg.is_active ? (
                      <Chip size="small" color="success" label="active" />
                    ) : (
                      <Chip size="small" label="inactive" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Refresh membership now">
                      <span>
                        <IconButton size="small" onClick={() => refresh(seg.id)} disabled={refreshingId === seg.id}>
                          {refreshingId === seg.id ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      size="small"
                      type="button"
                      disabled={editLoadingId === seg.id}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void openEdit(seg);
                      }}
                    >
                      {editLoadingId === seg.id ? 'Loading…' : 'Edit'}
                    </Button>
                    <Switch size="small" checked={seg.is_active} onChange={() => toggleActive(seg)} />
                    {!seg.is_system && (
                      <IconButton size="small" onClick={() => remove(seg)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {segments.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No segments yet. Seed migration 405 ships 40+ canonical segments.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        fullWidth
        maxWidth="md"
        slotProps={{
          root: {
            sx: { zIndex: 1400 },
          },
        }}
      >
        <DialogTitle>{editing ? `Edit: ${editing.name}` : 'New Segment'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField label="Name" value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              disabled={editing?.is_system}
              fullWidth size="small" required />
            <TextField label="Description" value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              fullWidth size="small" multiline minRows={2} />
            <Divider>Filter</Divider>
            <GroupEditor
              group={form.filter_json}
              allFields={allFields}
              onChange={g => setForm({ ...form, filter_json: g })}
            />
            {previewError && <Alert severity="error">{previewError}</Alert>}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button variant="outlined" startIcon={<PreviewIcon />} onClick={runPreview} disabled={previewLoading}>
                {previewLoading ? 'Counting…' : 'Preview count'}
              </Button>
              {previewCount !== null && (
                <Typography><strong>{previewCount.toLocaleString()}</strong> users match</Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}
            sx={{ bgcolor: '#c2410c', '&:hover': { bgcolor: '#9a3412' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// -------- Group / Condition recursive editor --------

const OPS: Op[] = [
  'eq', 'ne', 'gt', 'gte', 'lt', 'lte',
  'in', 'not_in',
  'is_null', 'is_not_null',
  'contains', 'starts_with', 'ends_with',
  'within_days', 'before_days', 'after_days',
];

function isGroup(node: Group | Condition): node is Group {
  return (node as Group).conditions !== undefined && ((node as Group).op === 'AND' || (node as Group).op === 'OR');
}

const GroupEditor: React.FC<{
  group: Group;
  allFields: Array<{ field: string; aggregate?: boolean }>;
  onChange: (g: Group) => void;
  depth?: number;
}> = ({ group, allFields, onChange, depth = 0 }) => {
  const conditions = Array.isArray(group.conditions) ? group.conditions : [];
  const update = (i: number, node: Group | Condition) => {
    const copy = { ...group, conditions: [...conditions] };
    copy.conditions[i] = node;
    onChange(copy);
  };
  const remove = (i: number) => {
    const copy = { ...group, conditions: conditions.filter((_, idx) => idx !== i) };
    onChange(copy);
  };
  const addCondition = () => onChange({
    ...group,
    conditions: [...conditions, { field: 'users.is_active', op: 'eq', value: true }]
  });
  const addGroup = () => onChange({
    ...group,
    conditions: [...conditions, { op: 'AND', conditions: [{ field: 'users.is_active', op: 'eq', value: true }] }]
  });

  return (
    <Card variant="outlined" sx={{ p: 1, borderColor: depth === 0 ? '#c2410c' : 'divider', borderWidth: depth === 0 ? 2 : 1 }}>
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small">
            <Select
              value={group.op}
              onChange={e => onChange({ ...group, op: e.target.value as 'AND' | 'OR' })}
              MenuProps={SEGMENT_BUILDER_SELECT_MENU_PROPS}
            >
              <MenuItem value="AND">Match ALL (AND)</MenuItem>
              <MenuItem value="OR">Match ANY (OR)</MenuItem>
            </Select>
          </FormControl>
          <Button size="small" startIcon={<AddIcon />} onClick={addCondition}>Condition</Button>
          <Button size="small" startIcon={<AddIcon />} onClick={addGroup}>Group</Button>
        </Box>
        {conditions.map((node, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              {isGroup(node) ? (
                <GroupEditor group={node} allFields={allFields} depth={depth + 1}
                  onChange={g => update(i, g)} />
              ) : (
                <ConditionEditor
                  condition={node as Condition}
                  allFields={allFields}
                  onChange={c => update(i, c)}
                />
              )}
            </Box>
            <IconButton size="small" onClick={() => remove(i)}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
        ))}
      </Stack>
    </Card>
  );
};

const ConditionEditor: React.FC<{
  condition: Condition;
  allFields: Array<{ field: string; aggregate?: boolean }>;
  onChange: (c: Condition) => void;
}> = ({ condition, allFields = [], onChange }) => {
  const isAggregate = !!allFields.find(f => f.field === condition.field)?.aggregate;
  const needsValue = !['is_null', 'is_not_null'].includes(condition.op);
  const isArrayOp = ['in', 'not_in'].includes(condition.op);

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <FormControl size="small" sx={{ minWidth: 260 }}>
        <InputLabel>Field</InputLabel>
        <Select
          label="Field"
          value={condition.field}
          MenuProps={SEGMENT_BUILDER_SELECT_MENU_PROPS}
          onChange={e => {
            const nextField = e.target.value as string;
            const nowAggregate = !!allFields.find(f => f.field === nextField)?.aggregate;
            onChange({ ...condition, field: nextField, aggregate: nowAggregate ? (condition.aggregate || 'any') : undefined });
          }}>
          {allFields.map(f => (
            <MenuItem key={f.field} value={f.field}>
              {f.aggregate ? `Σ  ${f.field}` : f.field}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {isAggregate && (
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Agg</InputLabel>
          <Select
            label="Agg"
            value={condition.aggregate || 'any'}
            MenuProps={SEGMENT_BUILDER_SELECT_MENU_PROPS}
            onChange={e => onChange({ ...condition, aggregate: e.target.value as 'any' | 'none' | 'count' })}
          >
            <MenuItem value="any">any</MenuItem>
            <MenuItem value="none">none</MenuItem>
            <MenuItem value="count">count</MenuItem>
          </Select>
        </FormControl>
      )}
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Op</InputLabel>
        <Select
          label="Op"
          value={condition.op}
          MenuProps={SEGMENT_BUILDER_SELECT_MENU_PROPS}
          onChange={e => onChange({ ...condition, op: e.target.value as Op })}
        >
          {OPS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </Select>
      </FormControl>
      {needsValue && (
        isArrayOp ? (
          <TextField size="small" label="values (comma-separated)" sx={{ minWidth: 220 }}
            value={Array.isArray(condition.value) ? (condition.value as unknown[]).join(',') : ''}
            onChange={e => onChange({ ...condition, value: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
          />
        ) : (
          <TextField size="small" label="value" sx={{ minWidth: 180 }}
            value={condition.value === undefined || condition.value === null ? '' : String(condition.value)}
            onChange={e => {
              const raw = e.target.value;
              let parsed: unknown = raw;
              if (raw === 'true') parsed = true;
              else if (raw === 'false') parsed = false;
              else if (raw !== '' && !isNaN(Number(raw))) parsed = Number(raw);
              onChange({ ...condition, value: parsed });
            }}
          />
        )
      )}
      <FormControlLabel
        control={<Switch size="small" checked={condition.op.startsWith('not_') || condition.op === 'ne'} disabled />}
        label=""
        sx={{ m: 0 }}
      />
    </Box>
  );
};

export default SegmentBuilder;
